import { useState, useEffect, useRef, ChangeEvent, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authHref, clearAuthFrom, useAuthNextPath } from '../utils/authRedirect.ts';
import './css/LoginPage.css';
import '../components/css/Navigation.css';
import SiteLogo from '../components/SiteLogo.tsx';
import axios from 'axios';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { API_BASE } from '../utils/apiEndpoints';
import { startYandexOAuth, startVkOAuth, useYandexOAuthErrorKey } from '../utils/yandexOAuth.ts';
import { AuthTermsCheckbox } from '../components/AuthTermsCheckbox.tsx';
import { NeuralNetBackground } from '../components/NeuralNetBackground.tsx';

function LoginPage() {
  const { t, currentLanguage, setLanguage } = useTranslation();
  const { login } = useAuth();
  const oauthErrorKey = useYandexOAuthErrorKey();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const nextPath = useAuthNextPath();

  const languagesToRender = ['ru', 'en'];
  const languageLabel: Record<string, string> = {
    ru: 'RU',
    en: 'EN',
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target as Node)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageSelect = (lang: string) => {
    setLanguage(lang);
    setLangMenuOpen(false);
  };

  useEffect(() => {
    const saved = localStorage.getItem('loginEmail');
    if (saved) {
      setFormData((prev) => ({ ...prev, email: saved }));
      setRememberMe(true);
    }
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API_BASE}/api/login/login`,
        { email: formData.email, password: formData.password },
        { withCredentials: true }
      );
      if (response.data.success) {
        if (rememberMe) {
          localStorage.setItem('loginEmail', formData.email);
        } else {
          localStorage.removeItem('loginEmail');
        }
        const profileRes = await axios.get(`${API_BASE}/api/profile/profile`, {
          withCredentials: true,
        });
        const p = profileRes.data;
        login({
          id: String(p.id),
          username: p.username,
          email: p.email,
          avatar: p.avatar,
          role: p.role || 'user',
        });
        clearAuthFrom();
        navigate(nextPath);
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: unknown } };
      const raw = axiosErr.response?.data;
      const msg = typeof raw === 'string' ? raw : (raw as { message?: string })?.message;
      if (msg === 'login.emailNotVerified') setError(t('login.emailNotVerified'));
      else if (msg === 'login.invalidCredentials') setError(t('login.invalidCredentials'));
      else if (msg === 'login.serverError') setError(t('login.serverError'));
      else setError(msg || t('login.error'));
    }
  };

  const year = new Date().getFullYear();

  return (
    <div className="login-page">
      <nav className="sciplex-nav login-page__nav">
        <div className="nav-container login-page__nav-container">
          <div className="nav-logo">
            <SiteLogo />
          </div>

          <div className="nav-links-wrap" aria-hidden />

          <div className="language-switcher" ref={langMenuRef}>
            <button
              type="button"
              className="language-select language-select-btn"
              onClick={() => setLangMenuOpen((v) => !v)}
              aria-expanded={langMenuOpen}
              aria-haspopup="listbox"
              aria-label="Выбор языка"
            >
              <span className="language-select-code">
                {languageLabel[currentLanguage] || currentLanguage.toUpperCase()}
              </span>
            </button>
            <div
              className={`language-dropdown ${langMenuOpen ? 'language-dropdown-open' : ''}`}
              role="listbox"
              aria-label="Языки"
            >
              {languagesToRender.map((lang) => (
                <button
                  key={lang}
                  type="button"
                  role="option"
                  aria-selected={currentLanguage === lang}
                  className={`language-option ${currentLanguage === lang ? 'language-option-active' : ''}`}
                  onClick={() => handleLanguageSelect(lang)}
                >
                  <span>{languageLabel[lang] || lang}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      <div className="login-page__body">
        <NeuralNetBackground />
        <div className="login-page__inner">
        <div className="login-page__grid">
        <aside className="login-page__hero">
          <div className="login-hero__noise" aria-hidden />
          <p className="login-hero__tagline">{t('login.heroTagline')}</p>
          <h1 className="login-hero__title">
            <span className="login-hero__title-line login-hero__title-line--light">{t('login.heroTitleLine1')}</span>
            <span className="login-hero__title-line login-hero__title-line--accent">{t('login.heroTitleLine2')}</span>
          </h1>
          <p className="login-hero__lead">{t('login.heroDescription')}</p>
        </aside>

        <div className="login-page__panel">
          <div className="login-card">
            <h2 className="login-card__title">{t('login.title')}</h2>

            {(error || oauthErrorKey) && (
              <div className="login-card__error">{error || (oauthErrorKey ? t(oauthErrorKey) : '')}</div>
            )}

            <form onSubmit={handleSubmit} className="login-form" noValidate>
              <div className="login-field">
                <label className="login-field__label" htmlFor="login-email">
                  {t('login.email')}
                </label>
                <div className="login-field__control">
                  <input
                    id="login-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="login-field__input"
                    placeholder={t('login.emailPlaceholder')}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="login-field">
                <label className="login-field__label" htmlFor="login-password">
                  {t('login.password')}
                </label>
                <div className="login-field__control">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="login-field__input login-field__input--with-toggle"
                    placeholder={t('login.passwordPlaceholder')}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="login-field__toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="login-form__row">
                <label className="login-check">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="login-check__input"
                  />
                  <span className="login-check__box" aria-hidden />
                  <span>{t('login.rememberMe')}</span>
                </label>
                <Link to={authHref('/forgot-password', nextPath)} className="login-form__link">
                  {t('login.forgotPassword')}
                </Link>
              </div>

              <div className="login-submit-wrap">
                <button type="submit" className="login-submit">
                  <span className="login-submit__label">{t('login.button')}</span>
                </button>
              </div>
            </form>

            <div className="login-divider">
              <span>{t('login.orContinueWith')}</span>
            </div>

            <AuthTermsCheckbox
              id="login-oauth-terms"
              checked={agreedToTerms}
              onChange={setAgreedToTerms}
              t={t}
            />

            <div className="login-social">
              <button
                type="button"
                className="login-social__btn login-social__btn--vk"
                disabled={!agreedToTerms}
                onClick={() => {
                  if (!agreedToTerms) {
                    setError(t('login.oauthTermsRequired'));
                    return;
                  }
                  startVkOAuth({ agreed: true, next: nextPath });
                }}
              >
                <img src="/linked/vk.png" alt="" className="login-social__logo" />
                {t('login.continueVk')}
              </button>
              <button
                type="button"
                className="login-social__btn login-social__btn--yandex"
                disabled={!agreedToTerms}
                onClick={() => {
                  if (!agreedToTerms) {
                    setError(t('login.oauthTermsRequired'));
                    return;
                  }
                  startYandexOAuth({ agreed: true, next: nextPath });
                }}
              >
                <img src="/linked/yandex.png" alt="" className="login-social__logo" />
                {t('login.continueYandex')}
              </button>
            </div>

            <p className="login-card__register">
              {t('login.noAccount')}{' '}
              <Link to={authHref('/register', nextPath)} className="login-card__register-link">
                {t('login.register')}
              </Link>
            </p>
          </div>
        </div>
      </div>
      </div>

      <footer className="login-page__footer">
        <p className="login-page__copyright">
          © {year} Sciplex. {t('login.footerRights')}
        </p>
        <p className="login-page__legal">
          <Link to="/privacy">{t('login.privacyLink')}</Link>
          <span className="login-page__legal-sep" aria-hidden>
            |
          </span>
          <Link to="/terms">{t('login.termsLink')}</Link>
        </p>
      </footer>
      </div>
    </div>
  );
}

export default LoginPage;
